import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = [
    "bubble",
    "drawer",
    "backdrop",
    "header",
    "messageList",
    "input",
    "conversationList",
    "typingIndicator",
    "conversationTitle",
    "emptyState",
    "historyPanel",
    "fullscreenIcon",
  ]

  static values = {
    agentAvatar: String,
  }

  connect() {
    this.currentConversationId = null
    this.isStreaming = false
    this.currentAssistantMessage = null
    this.charts = []
    this.historyOpen = false
    this.isFullscreen = false
    this.subscribe()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    this.destroyCharts()
  }

  // ActionCable subscription
  subscribe() {
    let consumer
    try {
      consumer = window.__chatConsumer || createConsumer()
      window.__chatConsumer = consumer
    } catch (e) {
      console.warn("[Chat] Failed to create ActionCable consumer", e)
      return
    }

    this.subscription = consumer.subscriptions.create(
      { channel: "ChatChannel" },
      {
        connected: () => console.log("[Chat] Cable connected"),
        disconnected: () => console.log("[Chat] Cable disconnected"),
        rejected: () => console.warn("[Chat] Cable rejected"),
        received: (data) => this.handleReceived(data),
      }
    )
  }

  handleReceived(data) {
    switch (data.type) {
      case "chunk":
        this.handleChunk(data)
        break
      case "complete":
        this.handleComplete(data)
        break
      case "error":
        this.handleError(data)
        break
    }
  }

  handleChunk(data) {
    if (!this.currentAssistantMessage) {
      this.hideTypingIndicator()
      this.currentAssistantMessage = this.appendMessage(
        "assistant",
        "",
        false
      )
    }
    const contentEl = this.currentAssistantMessage.querySelector(
      ".chat-message-content"
    )
    if (contentEl) {
      contentEl.textContent += data.content
    }
    this.scrollToBottom()
  }

  handleComplete(data) {
    this.isStreaming = false
    this.enableInput()
    this.hideTypingIndicator()

    if (this.currentAssistantMessage) {
      const contentEl = this.currentAssistantMessage.querySelector(
        ".chat-message-content"
      )
      if (contentEl) {
        contentEl.innerHTML = this.renderMarkdown(
          data.content || contentEl.textContent
        )
      }
      if (data.chart) {
        this.renderChart(data.chart, this.currentAssistantMessage)
      }
    }

    this.currentAssistantMessage = null

    if (data.conversation_id) {
      this.currentConversationId = data.conversation_id
    }
    if (data.title) {
      this.updateConversationTitle(data.title)
    }

    this.scrollToBottom()
  }

  handleError(data) {
    this.isStreaming = false
    this.enableInput()
    this.hideTypingIndicator()
    this.currentAssistantMessage = null

    this.appendMessage("assistant", `Sorry, an error occurred: ${data.error}`)
    this.scrollToBottom()
  }

  // UI Actions
  toggle() {
    const drawer = this.drawerTarget
    const isHidden = drawer.classList.contains("hidden")

    if (isHidden) {
      drawer.classList.remove("hidden")
      if (this.hasBackdropTarget) this.backdropTarget.classList.remove("hidden")
      requestAnimationFrame(() => {
        drawer.classList.remove("translate-y-4", "opacity-0")
        drawer.classList.add("translate-y-0", "opacity-100")
      })
      this.inputTarget.focus()
    } else {
      // Close history panel if open
      if (this.historyOpen) this.toggleHistory()
      // Exit fullscreen if active
      if (this.isFullscreen) this.toggleFullscreen()
      if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
      drawer.classList.add("translate-y-4", "opacity-0")
      drawer.classList.remove("translate-y-0", "opacity-100")
      setTimeout(() => drawer.classList.add("hidden"), 200)
    }
  }

  toggleHistory() {
    if (!this.hasHistoryPanelTarget) return

    this.historyOpen = !this.historyOpen
    if (this.historyOpen) {
      this.loadConversations()
      this.historyPanelTarget.classList.remove("hidden")
    } else {
      this.historyPanelTarget.classList.add("hidden")
    }
  }

  toggleFullscreen() {
    const drawer = this.drawerTarget
    this.isFullscreen = !this.isFullscreen

    if (this.isFullscreen) {
      // Switch to fullscreen modal
      drawer.classList.remove(
        "bottom-20",
        "right-6",
        "w-[400px]",
        "h-[600px]",
        "max-h-[80vh]",
        "rounded-2xl",
        "translate-y-4"
      )
      drawer.classList.add(
        "inset-0",
        "w-full",
        "h-full",
        "max-h-full",
        "z-50",
        "rounded-none"
      )

      // Remove rounded corners from header and history panel
      if (this.hasHeaderTarget) {
        this.headerTarget.classList.remove("rounded-t-2xl")
      }
      if (this.hasHistoryPanelTarget) {
        this.historyPanelTarget.classList.remove("rounded-b-2xl")
      }

      // Hide the bubble
      this.bubbleTarget.classList.add("hidden")

      // Update icon
      if (this.hasFullscreenIconTarget) {
        this.fullscreenIconTarget.setAttribute("icon", "ph:arrows-in")
      }
    } else {
      // Restore drawer classes
      drawer.classList.remove(
        "inset-0",
        "w-full",
        "h-full",
        "max-h-full",
        "z-50",
        "rounded-none"
      )
      drawer.classList.add(
        "bottom-20",
        "right-6",
        "w-[400px]",
        "h-[600px]",
        "max-h-[80vh]",
        "rounded-2xl"
      )

      // Restore rounded corners
      if (this.hasHeaderTarget) {
        this.headerTarget.classList.add("rounded-t-2xl")
      }
      if (this.hasHistoryPanelTarget) {
        this.historyPanelTarget.classList.add("rounded-b-2xl")
      }

      // Show the bubble
      this.bubbleTarget.classList.remove("hidden")

      // Update icon
      if (this.hasFullscreenIconTarget) {
        this.fullscreenIconTarget.setAttribute("icon", "ph:arrows-out")
      }
    }
  }

  async sendMessage(event) {
    if (event) {
      if (event.type === "keydown") {
        if (event.key !== "Enter" || event.shiftKey) return
        event.preventDefault()
      }
      if (event.type === "submit") {
        event.preventDefault()
      }
    }

    const content = this.inputTarget.value.trim()
    if (!content || this.isStreaming) return

    this.isStreaming = true
    this.disableInput()

    this.appendMessage("user", content)
    this.inputTarget.value = ""
    this.autoResizeInput()
    this.hideEmptyState()

    this.showTypingIndicator()
    this.scrollToBottom()

    this.subscription.perform("send_message", {
      content: content,
      conversation_id: this.currentConversationId,
    })
  }

  async newConversation() {
    // Close history panel if open
    if (this.historyOpen) this.toggleHistory()

    try {
      const response = await fetch("/chats", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })
      const data = await response.json()
      this.currentConversationId = data.id
      this.clearMessages()
      this.showEmptyState()
      this.updateConversationTitle("Radiant Assistant")
      this.inputTarget.focus()
    } catch (e) {
      console.error("[Chat] Failed to create conversation:", e)
    }
  }

  async loadConversation(event) {
    const id = event.currentTarget.dataset.conversationId
    if (!id) return

    try {
      const response = await fetch(`/chats/${id}`, {
        headers: { Accept: "application/json" },
      })
      const data = await response.json()

      this.currentConversationId = data.id
      this.clearMessages()
      this.updateConversationTitle(data.title || "Conversation")

      if (data.messages.length === 0) {
        this.showEmptyState()
      } else {
        this.hideEmptyState()
        data.messages.forEach((msg) => {
          const el = this.appendMessage(msg.role, msg.content)
          if (msg.chart) {
            this.renderChart(msg.chart, el)
          }
        })
      }

      // Close history panel after selecting
      if (this.historyOpen) this.toggleHistory()
      this.scrollToBottom()
    } catch (e) {
      console.error("[Chat] Failed to load conversation:", e)
    }
  }

  async deleteConversation(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.conversationId
    if (!id) return

    try {
      await fetch(`/chats/${id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this.csrfToken },
      })

      if (this.currentConversationId == id) {
        this.currentConversationId = null
        this.clearMessages()
        this.showEmptyState()
        this.updateConversationTitle("Radiant Assistant")
      }

      this.loadConversations()
    } catch (e) {
      console.error("[Chat] Failed to delete conversation:", e)
    }
  }

  async loadConversations() {
    try {
      const response = await fetch("/chats", {
        headers: { Accept: "application/json" },
      })
      const conversations = await response.json()
      this.renderConversationList(conversations)
    } catch (e) {
      console.error("[Chat] Failed to load conversations:", e)
    }
  }

  // Rendering helpers
  appendMessage(role, content, renderContent = true) {
    const wrapper = document.createElement("div")
    wrapper.className = `chat ${role === "user" ? "chat-end" : "chat-start"}`

    if (role === "assistant") {
      wrapper.innerHTML = `
        <div class="chat-image">
          <div class="w-8 h-8 rounded-full bg-base-200 flex items-center justify-center">
            <img src="${this.agentAvatarValue}" alt="Radiant" class="w-4 h-4 chat-agent-avatar" />
          </div>
        </div>
        <div class="chat-bubble bg-base-200 text-base-content overflow-hidden" style="max-width: min(80%, 320px);">
          <div class="chat-message-content prose prose-sm overflow-x-hidden break-words" style="max-width: 100%;">${renderContent ? this.renderMarkdown(content) : ""}</div>
        </div>
      `
    } else {
      wrapper.innerHTML = `
        <div class="chat-bubble">
          <div class="chat-message-content">${this.escapeHtml(content)}</div>
        </div>
      `
    }

    // Insert before the typing indicator to keep it at the bottom
    if (this.hasTypingIndicatorTarget) {
      this.messageListTarget.insertBefore(wrapper, this.typingIndicatorTarget)
    } else {
      this.messageListTarget.appendChild(wrapper)
    }
    this.scrollToBottom()
    return wrapper
  }

  renderConversationList(conversations) {
    if (!this.hasConversationListTarget) return

    if (conversations.length === 0) {
      this.conversationListTarget.innerHTML = `
        <div class="text-sm text-base-content/50 text-center py-8">No conversations yet</div>
      `
      return
    }

    this.conversationListTarget.innerHTML = conversations
      .map(
        (c) => `
      <div class="flex items-center group cursor-pointer hover:bg-base-200 rounded-lg px-3 py-2 transition-colors ${this.currentConversationId == c.id ? "bg-base-200" : ""}"
           data-conversation-id="${c.id}"
           data-action="click->chat#loadConversation">
        <iconify-icon icon="ph:chat-text" class="text-base-content/40 mr-2 flex-shrink-0"></iconify-icon>
        <div class="flex-1 truncate text-sm">${this.escapeHtml(c.title)}</div>
        <button class="btn btn-ghost btn-xs opacity-0 group-hover:opacity-100 transition-opacity"
                data-conversation-id="${c.id}"
                data-action="click->chat#deleteConversation">
          <iconify-icon icon="ph:trash" class="text-sm"></iconify-icon>
        </button>
      </div>
    `
      )
      .join("")
  }

  updateConversationTitle(title) {
    if (this.hasConversationTitleTarget) {
      this.conversationTitleTarget.textContent = title
    }
  }

  // Chart rendering
  async renderChart(chartConfig, parentEl) {
    const { Chart, registerables } = await import("chart.js")
    Chart.register(...registerables)

    const container = document.createElement("div")
    container.className = "my-3 w-full cursor-pointer group relative"
    container.title = "Click to expand"

    const canvas = document.createElement("canvas")
    canvas.style.minHeight = "200px"
    container.appendChild(canvas)

    // Expand overlay hint
    const expandHint = document.createElement("div")
    expandHint.className =
      "absolute inset-0 flex items-center justify-center bg-base-300/0 group-hover:bg-base-300/30 transition-colors rounded-lg pointer-events-none"
    expandHint.innerHTML =
      '<iconify-icon icon="ph:arrows-out" class="text-2xl opacity-0 group-hover:opacity-70 transition-opacity text-base-content"></iconify-icon>'
    container.appendChild(expandHint)

    const bubbleEl = parentEl.querySelector(".chat-bubble")
    if (bubbleEl) {
      bubbleEl.appendChild(container)
    }

    const chart = new Chart(canvas, {
      type: chartConfig.type || "bar",
      data: chartConfig.data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: !!chartConfig.title,
            text: chartConfig.title || "",
          },
        },
        scales:
          chartConfig.type === "pie" || chartConfig.type === "doughnut"
            ? {}
            : {
                y: { beginAtZero: true },
              },
      },
    })

    this.charts.push(chart)

    // Click to expand chart fullscreen
    container.addEventListener("click", () => {
      this.showChartFullscreen(chartConfig, Chart)
    })
  }

  showChartFullscreen(chartConfig, ChartJS) {
    // Create fullscreen overlay
    const overlay = document.createElement("div")
    overlay.className =
      "fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-6 animate-in fade-in"

    const modal = document.createElement("div")
    modal.className =
      "bg-base-100 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"

    // Header
    const header = document.createElement("div")
    header.className =
      "flex items-center justify-between px-4 py-3 border-b border-base-300"
    header.innerHTML = `
      <span class="font-semibold text-sm">${this.escapeHtml(chartConfig.title || "Chart")}</span>
      <div class="flex items-center gap-1">
        <button class="btn btn-ghost btn-sm gap-1 chart-download-btn" title="Download as image">
          <iconify-icon icon="ph:download-simple" class="text-lg"></iconify-icon>
          <span class="text-xs">Download</span>
        </button>
        <button class="btn btn-ghost btn-sm btn-circle chart-close-btn" title="Close">
          <iconify-icon icon="ph:x" class="text-lg"></iconify-icon>
        </button>
      </div>
    `
    modal.appendChild(header)

    // Chart container
    const chartContainer = document.createElement("div")
    chartContainer.className = "flex-1 p-6 min-h-0"
    const fullCanvas = document.createElement("canvas")
    chartContainer.appendChild(fullCanvas)
    modal.appendChild(chartContainer)

    overlay.appendChild(modal)
    document.body.appendChild(overlay)

    // Render full-size chart
    const fullChart = new ChartJS(fullCanvas, {
      type: chartConfig.type || "bar",
      data: JSON.parse(JSON.stringify(chartConfig.data)),
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          title: {
            display: !!chartConfig.title,
            text: chartConfig.title || "",
            font: { size: 16 },
          },
        },
        scales:
          chartConfig.type === "pie" || chartConfig.type === "doughnut"
            ? {}
            : {
                y: { beginAtZero: true },
              },
      },
    })

    // Download button
    header.querySelector(".chart-download-btn").addEventListener("click", () => {
      const link = document.createElement("a")
      link.download = `${(chartConfig.title || "chart").replace(/[^a-zA-Z0-9]/g, "_")}.png`
      link.href = fullCanvas.toDataURL("image/png", 1.0)
      link.click()
    })

    // Close handlers
    const close = () => {
      fullChart.destroy()
      overlay.remove()
    }
    header.querySelector(".chart-close-btn").addEventListener("click", close)
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close()
    })
    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape") close()
      },
      { once: true }
    )
  }

  destroyCharts() {
    this.charts.forEach((c) => c.destroy())
    this.charts = []
  }

  // Markdown rendering (tables + basic formatting)
  renderMarkdown(text) {
    if (!text) return ""

    let html = this.escapeHtml(text)

    // Markdown tables (must run before line breaks)
    html = this.renderTables(html)

    // Headers (## Header → <h> tags) — process line by line
    html = html.replace(/^#### (.+)$/gm, '<h4 class="font-semibold text-xs mt-3 mb-1">$1</h4>')
    html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-sm mt-3 mb-1">$1</h3>')
    html = html.replace(/^## (.+)$/gm, '<h2 class="font-bold text-sm mt-4 mb-1">$1</h2>')
    html = html.replace(/^# (.+)$/gm, '<h1 class="font-bold text-base mt-4 mb-2">$1</h1>')

    // Horizontal rules
    html = html.replace(/^---$/gm, '<hr class="my-2 border-base-300">')

    // Unordered lists (- item or * item)
    html = this.renderLists(html)

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")

    // Italic
    html = html.replace(/\*(.*?)\*/g, "<em>$1</em>")

    // Inline code
    html = html.replace(
      /`([^`]+)`/g,
      "<code class='bg-base-300 px-1 rounded text-sm'>$1</code>"
    )

    // Markdown links [text](url)
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="link link-primary">$1</a>'
    )

    // Line breaks (but not after block elements)
    html = html.replace(/\n/g, "<br>")

    // Clean up extra <br> around block elements
    html = html.replace(/(<\/h[1-4]>)<br>/g, "$1")
    html = html.replace(/(<\/li>)<br>/g, "$1")
    html = html.replace(/(<\/ul>)<br>/g, "$1")
    html = html.replace(/(<\/ol>)<br>/g, "$1")
    html = html.replace(/(<\/table>)<br>/g, "$1")
    html = html.replace(/(<hr[^>]*>)<br>/g, "$1")
    html = html.replace(/(<\/div>)<br>/g, "$1")
    html = html.replace(/<br>(<div class="chat-block)/g, "$1")

    return html
  }

  renderLists(text) {
    const lines = text.split("\n")
    let result = []
    let listLines = []
    let listType = null // "ul" or "ol"
    let inList = false

    const flushList = () => {
      if (listLines.length > 0) {
        const tag = listType || "ul"
        const cls = tag === "ol" ? "list-decimal" : "list-disc"
        let list = `<${tag} class="${cls} pl-4 my-1 space-y-0.5">`
        listLines.forEach((item) => {
          list += `<li class="text-xs">${item}</li>`
        })
        list += `</${tag}>`
        result.push(`<div class="chat-block relative my-1"><div class="max-h-[300px] overflow-y-auto">${list}</div><button onclick="this.closest('.chat-block').querySelector('ul,ol') && navigator.clipboard.writeText(this.closest('.chat-block').querySelector('ul,ol').innerText).then(() => { this.innerHTML = '<iconify-icon icon=&quot;ph:check&quot; class=&quot;text-xs&quot;></iconify-icon>'; setTimeout(() => { this.innerHTML = '<iconify-icon icon=&quot;ph:copy&quot; class=&quot;text-xs&quot;></iconify-icon>'; }, 1500) })" class="absolute top-1 right-1 btn btn-ghost btn-xs opacity-50 hover:opacity-100" title="Copy"><iconify-icon icon="ph:copy" class="text-xs"></iconify-icon></button></div>`)
        listLines = []
        listType = null
        inList = false
      }
    }

    for (const line of lines) {
      const trimmed = line.trim()
      const ulMatch = trimmed.match(/^[-*]\s+(.+)$/)
      const olMatch = trimmed.match(/^\d+\.\s+(.+)$/)

      if (ulMatch) {
        if (inList && listType !== "ul") flushList()
        inList = true
        listType = "ul"
        listLines.push(ulMatch[1])
      } else if (olMatch) {
        if (inList && listType !== "ol") flushList()
        inList = true
        listType = "ol"
        listLines.push(olMatch[1])
      } else {
        flushList()
        result.push(line)
      }
    }
    flushList()

    return result.join("\n")
  }

  renderTables(text) {
    const lines = text.split("\n")
    let result = []
    let tableLines = []
    let inTable = false

    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
        inTable = true
        tableLines.push(trimmed)
      } else {
        if (inTable) {
          result.push(this.buildTable(tableLines))
          tableLines = []
          inTable = false
        }
        result.push(line)
      }
    }

    if (inTable && tableLines.length > 0) {
      result.push(this.buildTable(tableLines))
    }

    return result.join("\n")
  }

  buildTable(lines) {
    if (lines.length < 2) return lines.join("\n")

    const parseRow = (line) =>
      line
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim())

    const headers = parseRow(lines[0])
    const dataStartIndex = lines[1] && lines[1].match(/^[\s|:-]+$/) ? 2 : 1
    const rows = lines.slice(dataStartIndex).map(parseRow)

    let table = '<table class="table table-sm table-zebra w-full">'
    table += "<thead><tr>"
    headers.forEach((h) => (table += `<th class="text-xs">${h}</th>`))
    table += "</tr></thead><tbody>"
    rows.forEach((row) => {
      table += "<tr>"
      row.forEach((cell) => (table += `<td class="text-xs">${cell}</td>`))
      table += "</tr>"
    })
    table += "</tbody></table>"

    return `<div class="chat-block relative my-2"><div class="overflow-x-auto max-h-[300px] overflow-y-auto">${table}</div><button onclick="this.closest('.chat-block').querySelector('table') && navigator.clipboard.writeText(this.closest('.chat-block').querySelector('table').innerText).then(() => { this.innerHTML = '<iconify-icon icon=&quot;ph:check&quot; class=&quot;text-xs&quot;></iconify-icon>'; setTimeout(() => { this.innerHTML = '<iconify-icon icon=&quot;ph:copy&quot; class=&quot;text-xs&quot;></iconify-icon>'; }, 1500) })" class="absolute top-1 right-1 btn btn-ghost btn-xs opacity-50 hover:opacity-100" title="Copy"><iconify-icon icon="ph:copy" class="text-xs"></iconify-icon></button></div>`
  }

  // Utility methods
  showTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.remove("hidden")
    }
  }

  hideTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add("hidden")
    }
  }

  showEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden")
    }
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  clearMessages() {
    this.destroyCharts()
    const messages = this.messageListTarget.querySelectorAll(
      ".chat:not([data-chat-target])"
    )
    messages.forEach((m) => m.remove())
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messageListTarget.scrollTop = this.messageListTarget.scrollHeight
    })
  }

  disableInput() {
    this.inputTarget.disabled = true
    const btn = this.element.querySelector("[data-chat-submit]")
    if (btn) btn.disabled = true
  }

  enableInput() {
    this.inputTarget.disabled = false
    const btn = this.element.querySelector("[data-chat-submit]")
    if (btn) btn.disabled = false
    this.inputTarget.focus()
  }

  autoResizeInput() {
    const input = this.inputTarget
    input.style.height = "auto"
    input.style.height = Math.min(input.scrollHeight, 120) + "px"
  }

  inputChanged() {
    this.autoResizeInput()
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
